import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLanguage, LANGUAGES } from "@/i18n/LanguageContext";
import { testimonialsData } from "@/i18n/testimonialsData";

export function scrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
}

function LangSwitcher({ mobile = false }) {
  const { lang, setLang } = useLanguage();
  return (
    <div data-testid={mobile ? "lang-switcher-mobile" : "lang-switcher"} className={`flex items-center ${mobile ? "gap-5" : "gap-3"}`}>
      {LANGUAGES.map((l) => (
        <button
          key={l.code}
          data-testid={`lang-${l.code}${mobile ? "-mobile" : ""}`}
          onClick={() => setLang(l.code)}
          className={`${mobile ? "text-sm" : "text-[10px]"} tracking-[0.2em] transition-colors duration-300 ${
            lang === l.code ? "text-brass" : "text-ink/40 hover:text-ink"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const td = testimonialsData[lang] || testimonialsData.fr;

  const LINKS = [
    { id: "expertise", label: t("nav.expertise") },
    { id: "services", label: t("nav.services") },
    { id: "langues", label: t("nav.langues") },
    { id: "temoignages", label: td.navLabel },
    { id: "apropos", label: t("nav.apropos") },
  ];

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(id), 350);
    } else {
      scrollToSection(id);
    }
  };

  return (
    <header data-testid="main-nav" className="fixed top-0 inset-x-0 z-50 bg-paper/80 backdrop-blur-xl border-b border-ink/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="font-serif text-xl tracking-tight text-ink">
          Oléna <span className="text-brass">Gurova</span>-Vacher
        </Link>
        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-[11px] uppercase tracking-[0.22em] text-ink/60 hover:text-ink transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <LangSwitcher />
          <button
            data-testid="nav-cta-devis"
            onClick={() => go("devis")}
            className="text-[11px] uppercase tracking-[0.22em] bg-slate2 text-paper px-6 py-3 hover:bg-brass hover:text-midnight transition-colors duration-300"
          >
            {t("nav.devis")}
          </button>
        </nav>
        <button data-testid="nav-mobile-toggle" className="md:hidden text-ink" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden bg-paper border-t border-ink/5 px-6 py-6 flex flex-col gap-5">
          {LINKS.map((l) => (
            <button key={l.id} data-testid={`nav-mobile-link-${l.id}`} onClick={() => go(l.id)} className="text-left text-sm uppercase tracking-[0.2em] text-ink/70">
              {l.label}
            </button>
          ))}
          <button data-testid="nav-mobile-cta-devis" onClick={() => go("devis")} className="text-left text-sm uppercase tracking-[0.2em] text-brass">
            {t("nav.devis")}
          </button>
          <LangSwitcher mobile />
        </div>
      )}
    </header>
  );
}
